// Interests / Aptitudes / Values quizzes
// Source items: Wadhwani Career Advisor questionnaires (12 / 20 / 14).
// Style: WeKIT™ "Nibble-app" — one question per screen, comic-strip
// micro-illustrations via emoji, big tap targets.
// Scoring: each option contributes additive weights to WeKIT™ Career DNA
// archetypes (Builder, Thinker, Advocate, Caregiver, Creator, Organizer,
// Leader, Adventurer). Likert questions multiply the question's base
// archetypes by option index (0..3).

/**
 * PORTED VERBATIM from v1 (`src/data/quizzes.ts`). Content untouched; the only
 * change is the import below — the archetype union now comes from the v1
 * bridge in this folder instead of v1's `careerDatabase`.
 */
import type { V1Archetype as Archetype } from "./archetypeMap";

export type QuestionKind = "pick4" | "pick2" | "likert";

export interface QuizOption {
  label: string;
  emoji?: string;
  weights?: Partial<Record<Archetype, number>>;
}

export interface QuizQuestion {
  id: string;
  prompt: string;
  kind: QuestionKind;
  options: QuizOption[];
  /** Likert only — base archetypes amplified by the chosen 0..3 intensity. */
  likertArchetypes?: Partial<Record<Archetype, number>>;
  /** Optional signal tag (e.g. "WLB", "Risk", "Tech"). Persisted, not boosted. */
  signalTag?: string;
}

export interface Quiz {
  id: "interests" | "aptitudes" | "values";
  title: string;
  emoji: string;
  intro: string;
  estimateMins: number;
  questions: QuizQuestion[];
}

const LIKERT_OPTIONS: QuizOption[] = [
  { label: "Not much", emoji: "👎" },
  { label: "Sometimes", emoji: "🤔" },
  { label: "Quite a bit", emoji: "👍" },
  { label: "A lot", emoji: "😍" },
];

// ─── INTERESTS — 12 questions ────────────────────────────────────────────
export const INTERESTS_QUIZ: Quiz = {
  id: "interests",
  title: "Interests",
  emoji: "🎨",
  intro: "Quick 2 mins. Tap what feels most like you — no right answers.",
  estimateMins: 2,
  questions: [
    {
      id: "i1",
      prompt: "In school or at home, what do you enjoy more?",
      kind: "pick4",
      options: [
        { label: "Fix something with tools (a fan, cycle)", emoji: "🔧", weights: { Builder: 3 } },
        { label: "Work with machines (mixer, computer)", emoji: "⚙️", weights: { Builder: 2, Thinker: 1 } },
        { label: "Do a computer/mobile task (typing, search)", emoji: "💻", weights: { Thinker: 2, Organizer: 1 } },
        { label: "Read and understand a lesson", emoji: "📖", weights: { Thinker: 3 } },
      ],
    },
    {
      id: "i2",
      prompt: "Doing hands-on work for some time (fixing, assembling)",
      kind: "likert",
      options: LIKERT_OPTIONS,
      likertArchetypes: { Builder: 1 },
    },
    {
      id: "i3",
      prompt: "What do you like doing more?",
      kind: "pick4",
      options: [
        { label: "Try a small experiment (mixing, testing)", emoji: "🧪", weights: { Thinker: 3 } },
        { label: "Solve a tricky question or puzzle", emoji: "🧩", weights: { Thinker: 2, Builder: 1 } },
        { label: "Check and compare info (prices, data)", emoji: "📊", weights: { Organizer: 2, Thinker: 1 } },
        { label: "Make something new (a small idea)", emoji: "💡", weights: { Creator: 3 } },
      ],
    },
    {
      id: "i4",
      prompt: "A device isn't working properly. What do you do?",
      kind: "pick4",
      options: [
        { label: "Find out the reason step by step", emoji: "🔍", weights: { Thinker: 3 } },
        { label: "Ask others what they think", emoji: "🗣️", weights: { Advocate: 2, Caregiver: 1 } },
        { label: "Note it and check later", emoji: "📝", weights: { Organizer: 3 } },
        { label: "Try a different way to use it", emoji: "🔄", weights: { Adventurer: 2, Creator: 1 } },
      ],
    },
    {
      id: "i5",
      prompt: "In your free time, what do you enjoy?",
      kind: "pick4",
      options: [
        { label: "Drawing or designing (poster, notebook cover)", emoji: "🎨", weights: { Creator: 3 } },
        { label: "Acting or performing", emoji: "🎭", weights: { Creator: 2, Leader: 1 } },
        { label: "Singing or creating music", emoji: "🎵", weights: { Creator: 3 } },
        { label: "Doing structured tasks (puzzles, patterns)", emoji: "🧩", weights: { Organizer: 2, Thinker: 1 } },
      ],
    },
    {
      id: "i6",
      prompt: "Which option do you like more?",
      kind: "pick2",
      options: [
        { label: "Do things your own way (try new styles)", emoji: "✨", weights: { Creator: 2, Adventurer: 1 } },
        { label: "Follow clear steps (a given method)", emoji: "📋", weights: { Organizer: 2, Builder: 1 } },
      ],
    },
    {
      id: "i7",
      prompt: "A friend near you doesn't understand a question. What do you do?",
      kind: "pick4",
      options: [
        { label: "Explain step by step (your way)", emoji: "👩‍🏫", weights: { Caregiver: 2, Advocate: 1 } },
        { label: "Show your answer as an example", emoji: "📄", weights: { Caregiver: 1, Organizer: 1 } },
        { label: "Finish your work first", emoji: "✅", weights: { Organizer: 2 } },
        { label: "Help a bit, then continue your work", emoji: "🤝", weights: { Caregiver: 1, Organizer: 1 } },
      ],
    },
    {
      id: "i8",
      prompt: "Explaining something to someone younger (sibling, classmate)",
      kind: "likert",
      options: LIKERT_OPTIONS,
      likertArchetypes: { Caregiver: 1, Advocate: 0.5 },
    },
    {
      id: "i9",
      prompt: "In a group activity, what do you usually do?",
      kind: "pick4",
      options: [
        { label: "Arrange and divide the work", emoji: "🗂️", weights: { Leader: 2, Organizer: 1 } },
        { label: "Share ideas to guide the task", emoji: "💬", weights: { Leader: 2, Advocate: 1 } },
        { label: "Help others in their parts", emoji: "🙌", weights: { Caregiver: 2, Advocate: 1 } },
        { label: "First see and understand, then join", emoji: "👀", weights: { Thinker: 2 } },
      ],
    },
    {
      id: "i10",
      prompt: "Which option do you like more?",
      kind: "pick2",
      options: [
        { label: "Speak and convince others (presenting)", emoji: "🎤", weights: { Leader: 2, Advocate: 1 } },
        { label: "Work quietly with information (checking details)", emoji: "🔬", weights: { Thinker: 2, Organizer: 1 } },
      ],
    },
    {
      id: "i11",
      prompt: "Your notes look messy. What do you do?",
      kind: "pick4",
      options: [
        { label: "Arrange neatly (headings, order)", emoji: "📑", weights: { Organizer: 3 } },
        { label: "Fill details carefully (tables, lists)", emoji: "📊", weights: { Organizer: 2, Thinker: 1 } },
        { label: "Make it look better (colours, layout)", emoji: "🎨", weights: { Creator: 2 } },
        { label: "Do enough to complete it", emoji: "⚡", weights: { Adventurer: 1 } },
      ],
    },
    {
      id: "i12",
      prompt: "Adventure and trying new places excite you",
      kind: "likert",
      options: LIKERT_OPTIONS,
      likertArchetypes: { Adventurer: 1 },
    },
  ],
};

// ─── APTITUDES — 20 questions ────────────────────────────────────────────
export const APTITUDES_QUIZ: Quiz = {
  id: "aptitudes",
  title: "Aptitudes",
  emoji: "⚡",
  intro: "Discover your strengths. About 3 mins.",
  estimateMins: 3,
  questions: [
    {
      id: "a1",
      prompt: "You get mixed information (a word problem). What do you do?",
      kind: "pick4",
      options: [
        { label: "Look for patterns and relations", emoji: "🔗", weights: { Thinker: 3 } },
        { label: "Break into steps and sort", emoji: "📋", weights: { Organizer: 2, Builder: 1 } },
        { label: "Discuss with someone", emoji: "💬", weights: { Advocate: 2 } },
        { label: "Start from the easy part", emoji: "🚀", weights: { Adventurer: 1, Builder: 1 } },
      ],
    },
    {
      id: "a2",
      prompt: "Which do you like more?",
      kind: "pick2",
      options: [
        { label: "Solving logic puzzles (patterns)", emoji: "🧩", weights: { Thinker: 2 } },
        { label: "Breaking a problem into steps", emoji: "📝", weights: { Builder: 1, Organizer: 1 } },
      ],
    },
    {
      id: "a3",
      prompt: "Someone didn't understand you. What do you do?",
      kind: "pick4",
      options: [
        { label: "Say it in different words", emoji: "🗨️", weights: { Advocate: 2, Creator: 1 } },
        { label: "Give a simple example", emoji: "🎯", weights: { Caregiver: 1, Thinker: 1 } },
        { label: "Ask what confused them", emoji: "❓", weights: { Caregiver: 2 } },
        { label: "Keep it short and move on", emoji: "⏩", weights: { Organizer: 1 } },
      ],
    },
    {
      id: "a4",
      prompt: "Speaking in front of others (class, presentation)",
      kind: "likert",
      options: LIKERT_OPTIONS,
      likertArchetypes: { Leader: 1, Advocate: 0.5 },
    },
    {
      id: "a5",
      prompt: "You need to compare numbers (prices, marks). What do you do?",
      kind: "pick4",
      options: [
        { label: "Calculate carefully", emoji: "🧮", weights: { Thinker: 2, Organizer: 1 } },
        { label: "Estimate and check", emoji: "📈", weights: { Thinker: 1, Adventurer: 1 } },
        { label: "Ask someone else", emoji: "🙋", weights: { Advocate: 1 } },
        { label: "Use a quick shortcut", emoji: "⚡", weights: { Adventurer: 2 } },
      ],
    },
    {
      id: "a6",
      prompt: "Which do you like more?",
      kind: "pick2",
      options: [
        { label: "Working with numbers (totals, comparisons)", emoji: "🔢", weights: { Thinker: 2 } },
        { label: "Working with words (explaining, writing)", emoji: "✍️", weights: { Creator: 2, Advocate: 1 } },
      ],
    },
    {
      id: "a7",
      prompt: "Visualising shapes and how things fit together",
      kind: "likert",
      options: LIKERT_OPTIONS,
      likertArchetypes: { Builder: 1, Creator: 0.5 },
    },
    {
      id: "a8",
      prompt: "The usual way is not working. What do you do?",
      kind: "pick4",
      options: [
        { label: "Try a different idea", emoji: "💡", weights: { Creator: 2, Adventurer: 1 } },
        { label: "Mix two ideas", emoji: "🎨", weights: { Creator: 3 } },
        { label: "Ask others", emoji: "🤝", weights: { Advocate: 1, Caregiver: 1 } },
        { label: "Try the same method again", emoji: "🔁", weights: { Organizer: 1, Builder: 1 } },
      ],
    },
    {
      id: "a9",
      prompt: "Coming up with new ideas on the spot",
      kind: "likert",
      options: LIKERT_OPTIONS,
      likertArchetypes: { Creator: 1, Adventurer: 0.5 },
    },
    {
      id: "a10",
      prompt: "Noticing small details others miss",
      kind: "likert",
      options: LIKERT_OPTIONS,
      likertArchetypes: { Organizer: 1, Thinker: 0.5 },
    },
    {
      id: "a11",
      prompt: "Two classmates disagree. What do you do?",
      kind: "pick4",
      options: [
        { label: "Help them talk calmly", emoji: "🕊️", weights: { Caregiver: 2, Advocate: 1 } },
        { label: "Keep focus on the task", emoji: "🎯", weights: { Organizer: 2 } },
        { label: "Listen before suggesting", emoji: "👂", weights: { Thinker: 1, Caregiver: 1 } },
        { label: "Continue your work", emoji: "💼", weights: { Organizer: 1 } },
      ],
    },
    {
      id: "a12",
      prompt: "Working with different people (group work)",
      kind: "likert",
      options: LIKERT_OPTIONS,
      likertArchetypes: { Advocate: 1, Caregiver: 0.5 },
    },
    {
      id: "a13",
      prompt: "Which do you prefer?",
      kind: "pick4",
      options: [
        { label: "Learn a new app/tool (like Excel)", emoji: "📱", weights: { Builder: 1, Thinker: 1 } },
        { label: "Check how a device works", emoji: "🔧", weights: { Builder: 2 } },
        { label: "Follow a simple guide", emoji: "📘", weights: { Organizer: 2 } },
        { label: "Learn from someone", emoji: "👨‍🏫", weights: { Caregiver: 1, Advocate: 1 } },
      ],
    },
    {
      id: "a14",
      prompt: "You get a new device. What do you do?",
      kind: "pick4",
      options: [
        { label: "Explore features", emoji: "🧭", weights: { Adventurer: 2, Builder: 1 } },
        { label: "Learn step by step", emoji: "🪜", weights: { Organizer: 2 } },
        { label: "Use it after someone shows me", emoji: "👀", weights: { Caregiver: 1 } },
        { label: "Use basic features first", emoji: "🟢", weights: { Organizer: 1 } },
      ],
    },
    {
      id: "a15",
      prompt: "Many tasks are due. What do you do?",
      kind: "pick4",
      options: [
        { label: "List and prioritise", emoji: "🗒️", weights: { Organizer: 3 } },
        { label: "Group similar tasks", emoji: "🗂️", weights: { Organizer: 2, Thinker: 1 } },
        { label: "Share work with others", emoji: "🤝", weights: { Leader: 2, Advocate: 1 } },
        { label: "Start with what feels urgent", emoji: "🔥", weights: { Adventurer: 2 } },
      ],
    },
    {
      id: "a16",
      prompt: "Which do you prefer?",
      kind: "pick4",
      options: [
        { label: "Plan order of work", emoji: "📅", weights: { Organizer: 2 } },
        { label: "Track deadlines", emoji: "⏰", weights: { Organizer: 2, Leader: 1 } },
        { label: "Improve look and presentation", emoji: "🎨", weights: { Creator: 2 } },
        { label: "Fix issues quickly", emoji: "🛠️", weights: { Builder: 2, Adventurer: 1 } },
      ],
    },
    {
      id: "a17",
      prompt: "You notice a small opportunity (selling/making). What do you do?",
      kind: "pick4",
      options: [
        { label: "Try it out in a small way", emoji: "🚀", weights: { Adventurer: 3, Leader: 1 } },
        { label: "Think how it could work", emoji: "🧠", weights: { Thinker: 2 } },
        { label: "Discuss with others", emoji: "💬", weights: { Advocate: 2 } },
        { label: "Keep idea for later", emoji: "📥", weights: { Organizer: 1 } },
      ],
    },
    {
      id: "a18",
      prompt: "Which do you like more?",
      kind: "pick2",
      options: [
        { label: "Trying new ideas or starting something on my own", emoji: "🌱", weights: { Adventurer: 2, Leader: 1 } },
        { label: "Working on tasks where steps are clearly given", emoji: "📐", weights: { Organizer: 2 } },
      ],
    },
    {
      id: "a19",
      prompt: "Which do you prefer?",
      kind: "pick4",
      options: [
        { label: "Work with hands (repair, make)", emoji: "🔨", weights: { Builder: 3 } },
        { label: "Use tools and equipment", emoji: "⚙️", weights: { Builder: 2 } },
        { label: "Work on screen", emoji: "💻", weights: { Thinker: 1, Creator: 1 } },
        { label: "Study from notes", emoji: "📚", weights: { Thinker: 2, Organizer: 1 } },
      ],
    },
    {
      id: "a20",
      prompt: "Hands-on tasks (fixing, building, cooking, using tools)",
      kind: "likert",
      options: LIKERT_OPTIONS,
      likertArchetypes: { Builder: 1 },
    },
  ],
};

// ─── VALUES — 14 questions ───────────────────────────────────────────────
export const VALUES_QUIZ: Quiz = {
  id: "values",
  title: "Values",
  emoji: "💎",
  intro: "What matters most to you in work and life. About 2 mins.",
  estimateMins: 2,
  questions: [
    {
      id: "v1",
      prompt: "Which do you like more?",
      kind: "pick2",
      options: [
        { label: "Time for work and personal life", emoji: "⚖️", weights: { Caregiver: 1, Organizer: 1 } },
        { label: "Spend more time on goals and work", emoji: "🚀", weights: { Leader: 1, Adventurer: 1 } },
      ],
      signalTag: "WLB",
    },
    {
      id: "v2",
      prompt: "Having time for rest, family, and study",
      kind: "likert",
      options: LIKERT_OPTIONS,
      likertArchetypes: { Caregiver: 0.8 },
      signalTag: "WLB",
    },
    {
      id: "v3",
      prompt: "You have a test soon. What do you do?",
      kind: "pick4",
      options: [
        { label: "Study topics in detail", emoji: "📚", weights: { Thinker: 2, Organizer: 1 } },
        { label: "Focus on important areas", emoji: "🎯", weights: { Organizer: 2 } },
        { label: "Discuss tough parts with someone", emoji: "🤝", weights: { Advocate: 2, Caregiver: 1 } },
        { label: "Plan study with breaks", emoji: "🧘", weights: { Caregiver: 1, Organizer: 1 } },
      ],
    },
    {
      id: "v4",
      prompt: "Which do you like more?",
      kind: "pick2",
      options: [
        { label: "Improve your performance", emoji: "📈", weights: { Leader: 2, Adventurer: 1 } },
        { label: "Keep things balanced", emoji: "⚖️", weights: { Caregiver: 1, Organizer: 1 } },
      ],
    },
    {
      id: "v5",
      prompt: "Which do you like more?",
      kind: "pick2",
      options: [
        { label: "Decide your own way", emoji: "🧭", weights: { Adventurer: 2, Leader: 1 } },
        { label: "Follow clear guidance", emoji: "🗺️", weights: { Organizer: 2 } },
      ],
    },
    {
      id: "v6",
      prompt: "Starting a new task. What do you do?",
      kind: "pick4",
      options: [
        { label: "Plan your own way", emoji: "✨", weights: { Adventurer: 2, Creator: 1 } },
        { label: "Take inputs, then decide", emoji: "🤔", weights: { Leader: 1, Thinker: 1 } },
        { label: "Follow given steps", emoji: "📋", weights: { Organizer: 2 } },
        { label: "Mix your way with some guidance", emoji: "🔀", weights: { Builder: 1, Creator: 1 } },
      ],
    },
    {
      id: "v7",
      prompt: "After good work, what feels better?",
      kind: "pick4",
      options: [
        { label: "Getting appreciation in front of others", emoji: "🏆", weights: { Leader: 2 } },
        { label: "People noticing your work", emoji: "👀", weights: { Creator: 1, Advocate: 1 } },
        { label: "Sharing credit with team", emoji: "🤝", weights: { Caregiver: 2, Advocate: 1 } },
        { label: "Feeling satisfied quietly", emoji: "🧘", weights: { Thinker: 2 } },
      ],
    },
    {
      id: "v8",
      prompt: "Being appreciated publicly (class, assembly)",
      kind: "likert",
      options: LIKERT_OPTIONS,
      likertArchetypes: { Leader: 0.8 },
    },
    {
      id: "v9",
      prompt: "Which setup do you prefer?",
      kind: "pick4",
      options: [
        { label: "People help each other often", emoji: "💞", weights: { Caregiver: 2, Advocate: 1 } },
        { label: "Ask for help anytime", emoji: "🙋", weights: { Advocate: 1, Caregiver: 1 } },
        { label: "Mostly work independently", emoji: "🚶", weights: { Thinker: 2 } },
        { label: "Some support, not always", emoji: "🤝", weights: { Organizer: 1 } },
      ],
    },
    {
      id: "v10",
      prompt: "Which feels better?",
      kind: "pick4",
      options: [
        { label: "Working in a team", emoji: "👥", weights: { Advocate: 2, Caregiver: 1 } },
        { label: "Having a mentor guide you", emoji: "🧑‍🏫", weights: { Caregiver: 2 } },
        { label: "Working on your own", emoji: "🧍", weights: { Thinker: 2 } },
        { label: "Following a clear process", emoji: "📐", weights: { Organizer: 2 } },
      ],
    },
    {
      id: "v11",
      prompt: "Which do you like more?",
      kind: "pick2",
      options: [
        { label: "Better pay for your work", emoji: "💰", weights: { Leader: 1, Builder: 1 } },
        { label: "Better support and environment", emoji: "🌿", weights: { Caregiver: 2 } },
      ],
      signalTag: "Pay vs Env",
    },
    {
      id: "v12",
      prompt: "Two opportunities. What do you look at first?",
      kind: "pick4",
      options: [
        { label: "Pay offered", emoji: "💵", weights: { Leader: 1, Builder: 1 } },
        { label: "Growth and learning chance", emoji: "📈", weights: { Thinker: 2, Adventurer: 1 } },
        { label: "Stability of the option", emoji: "🛡️", weights: { Organizer: 2 } },
        { label: "Overall balance", emoji: "⚖️", weights: { Caregiver: 1, Organizer: 1 } },
      ],
    },
    {
      id: "v13",
      prompt: "Which do you like more?",
      kind: "pick2",
      options: [
        { label: "Stable and dependable path", emoji: "🛤️", weights: { Organizer: 2, Caregiver: 1 } },
        { label: "Less certain but more upside", emoji: "🎢", weights: { Adventurer: 2, Leader: 1 } },
      ],
      signalTag: "Risk",
    },
    {
      id: "v14",
      prompt: "How important is predictability in your future?",
      kind: "likert",
      options: LIKERT_OPTIONS,
      likertArchetypes: { Organizer: 0.8 },
      signalTag: "Risk",
    },
  ],
};

export const ALL_QUIZZES: Quiz[] = [INTERESTS_QUIZ, APTITUDES_QUIZ, VALUES_QUIZ];

export type QuizAnswers = Record<string, number>; // questionId → option index

/**
 * Convert quiz answers into additive archetype boosts.
 * Boost values are capped per archetype so quizzes enrich — never override —
 * core psychometric scoring.
 */
export function quizAnswersToArchetypeBoost(
  quiz: Quiz,
  answers: QuizAnswers,
): Partial<Record<Archetype, number>> {
  const boost: Partial<Record<Archetype, number>> = {};
  const add = (a: Archetype, v: number) => {
    boost[a] = (boost[a] ?? 0) + v;
  };

  for (const q of quiz.questions) {
    const idx = answers[q.id];
    if (idx == null) continue;
    if (q.kind === "likert" && q.likertArchetypes) {
      // 0 → 0, 1 → 1×, 2 → 2×, 3 → 3×
      const mult = idx;
      for (const [a, w] of Object.entries(q.likertArchetypes)) {
        add(a as Archetype, (w ?? 0) * mult);
      }
    } else {
      const opt = q.options[idx];
      if (!opt?.weights) continue;
      for (const [a, w] of Object.entries(opt.weights)) {
        add(a as Archetype, w ?? 0);
      }
    }
  }

  // Cap per archetype
  const CAP = 12;
  for (const k of Object.keys(boost)) {
    boost[k as Archetype] = Math.min(boost[k as Archetype] ?? 0, CAP);
  }
  return boost;
}

export function mergeBoosts(
  ...boosts: Partial<Record<Archetype, number>>[]
): Partial<Record<Archetype, number>> {
  const out: Partial<Record<Archetype, number>> = {};
  for (const b of boosts) {
    for (const [a, v] of Object.entries(b)) {
      out[a as Archetype] = (out[a as Archetype] ?? 0) + (v ?? 0);
    }
  }
  return out;
}
